import { RequestApiDataIfNecessaryPayload } from 'services/api/api.types';
import { z } from 'zod';
import { DepositMode } from './recharges.config';

export type DepositModeKey = (typeof DepositMode)[keyof typeof DepositMode];

export const PamTopupErrorCode = {
  TopUpPatternNotFound: '10001',
  DepositAmountLimit: '10002',
  InvalidPromoCodeCharacters: '10003',
  CacheError: '10004',
  TopUpPatternSearchError: '10005',
  PromoCodeAlreadyUsed: '10006',
  PromoCodeStatusCountError: '10007',
  IncompleteUserRegistration: '10008',
  TopUpLimitsRetrievalError: '10009',
  DailyDepositLimitExceeded: '10010',
  DailyDepositAmountExceeded: '10011',
  WeeklyDepositAmountExceeded: '10012',
  WeeklyAntiMafiaDepositError: '10013',
  WeeklyAntiMafiaLimitExceeded: '10014',
  TotalDepositAmountError: '10015',
  LightAccountDepositExceeded: '10016',
  InvalidAmount: '10017',
  GenericError: '10018',
  TaxCodeMismatch: '10019',
  IntegrationObjectNotFound: '10020',
  RequiredFieldNotFound: '10021',
  UserStatusInvalid: '10022',
  NotConfiguredForAutomatic: '10023',
  TopupSearchError: '10024',
  WalletMappingRetrievalError: '10025',
  WalletMappingNotFound: '10026',
  RequestBodyNotFound: '10027',
  TopUpRetrievalError: '10028',
  TopUpNotFound: '10029',
  AccountCodeMismatch: '10030',
  RefundTimeLimitExceeded: '10031',
  OperationUndefined: '20009',
  AccountNotFound: '20002',
  IdNotValid: '20001',
  LimitOverWeek: '20124',
  LimitOverMonthLimit: '20125',
  LimitOverWeekProduct: '20126',
  LimitOverMonthProduct: '20127',
  TemporaryAutoexclusion: '20202',
  PermanentAutoexclusion: '20209',
  LimitNotSet: '20223',
  SecurityQuestionNotSet: '20224',
  DocExp: '20444',
  NotProt: '20443',
  DocExpOperationForbidden: '20445',
  GamingBreakTemp: '20993',
  GamingBreakPerm: '20992',
} as const;

export type TopupErrorCodeKey = keyof typeof PamTopupErrorCode;
export type TopupErrorCodeValue = (typeof PamTopupErrorCode)[TopupErrorCodeKey];

export const basePamResponseSchema = z.object({
  channel: z.number(),
  requestId: z.string(),
  code: z.string().optional(),
  message: z.string().optional(),
});

// Response of /cashier-core-ms/v1/topup/methods
const topupMethodSchema = z.object({
  gatewayId: z.number(),
  circuitId: z.number(),
  description: z.string(),
  minAmount: z.number(),
  maxAmount: z.number(),
  topupReferencesId: z.array(z.number()),
});

const topupBonusSchema = z.object({
  id: z.number(),
  code: z.string(),
  description: z.string(),
  info: z.string(),
  type: z.string(),
  promoTitle: z.string(),
  minDepositAmount: z.number(),
});

export const topupMethodsResponseSchema = basePamResponseSchema.extend({
  responseObject: z.array(topupMethodSchema),
  topups: z.record(z.string(), topupBonusSchema),
});

export type TopupMethod = z.infer<typeof topupMethodSchema>;
export type TopupBonus = z.infer<typeof topupBonusSchema>;
export type TopupMethodsResponse = z.infer<typeof topupMethodsResponseSchema>;

// Request payload of /cashier-core-ms/v1/topup/request
export type TopupRequestPayload = {
  amount: number;
  circuitId: number;
  gatewayId: number;
  promoCode?: string;
};

// Response of /cashier-core-ms/v1/topup/request
export const topupRequestResponseSchema = basePamResponseSchema.extend({
  responseObject: z.string(),
});

export type TopupRequestResponse = z.infer<typeof topupRequestResponseSchema>;

// Request payload of /cashier-core-ms/v1/topup/activation
export type TopupActivationPayload = {
  gatewayId: number;
  circuitId: number;
  pin: string;
  promoCode?: string;
};

// Response of /cashier-core-ms/v1/topup/activation
export const topupActivationResponseSchema = basePamResponseSchema;
export type TopupActivationResponse = z.infer<
  typeof topupActivationResponseSchema
>;

export interface CreateSubmitDepositDemandApiConfig {
  (
    formId: string,
    depId: string,
    formValues: { amount: number },
  ): RequestApiDataIfNecessaryPayload;
}
